import type { ProfileResponse } from '../../types/api'

/**
 * The facts a participant gave at registration, read-only.
 *
 * <p>Editing used to live here behind an Edit button. It was removed deliberately: `serviceType`
 * is a derived column that {@code NeedProjection} rewrites from the primary service row, so a
 * free-text edit here would be silently overwritten the next time a service changed - it is shown
 * beside each service in "What I teach" instead (see ServicesSection), computed the same way. Fee
 * range, experience and qualifications are genuinely the tutor's to change, and have their own
 * editor now too (see AboutSection) - shown there, not duplicated here as a second, read-only copy.
 * The fields that are genuinely the participant's to change belong beside the thing that owns
 * them - needs and services on their own pages, fee band on the service, locality in the address
 * step.
 */
export function RegistrationDetails({ profile }: { profile: ProfileResponse }) {
  const isSupply = profile.persona === 'SUPPLY'

  const rows: [string, string | undefined][] = isSupply
    ? [
        ['Email', profile.email],
        ['Mobile', profile.phone],
        ['Pincode', profile.pincode],
        ['Locality', profile.locality],
      ]
    : [
        ['Email', profile.email],
        ['Mobile', profile.phone],
        ['Pincode', profile.pincode],
        ['Class', profile.gradeBand],
        ['Date of birth', profile.dob],
        ['Board', profile.board],
        ['School', profile.schoolName],
        ['Stream', profile.stream],
        ['Locality', profile.locality],
      ]

  return (
    <div className="card-pad">
      <h2 className="font-bold text-ink-900">Your registration details</h2>

      <div className="mt-4 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
        {rows
          .filter(([, value]) => !!value)
          .map(([label, value]) => (
            <div key={label}>
              <div className="text-[10px] font-bold uppercase tracking-wide text-ink-400">{label}</div>
              <div className="break-words text-sm font-semibold text-ink-900">{value}</div>
            </div>
          ))}
      </div>
    </div>
  )
}
